import { Alert, Choicebox, Form, FormGroup, Heading, List, RadioGroup, TextInput } from '@ksyos/design-system';
import type { ICostInfoAndConsentState, IHomeAddress, IInvoiceAddress } from '@common/validation/costInfoAndConsent';
import type { ValidationErrors } from '@frontend/utils/validation';
import { PaymentMethod } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';

interface IPatientInvoicePreferenceProps {
    onBlur: (
        value: string | PaymentMethod | IHomeAddress | IInvoiceAddress,
        name: string,
        validateName?: string
    ) => void;
    onChange: (value: string | PaymentMethod | IHomeAddress | IInvoiceAddress, name: string) => void;
    state: ICostInfoAndConsentState;
    validationErrors: ValidationErrors;
}

export function PatientInvoicePreference(props: IPatientInvoicePreferenceProps) {
    const language = getSelectedLanguage();
    const responsiveWidthSmall = { width: ['30%', '20%'] };
    const responsiveWidthLarge = { width: ['100%', '60%'] };

    return (
        <>
            {props.state.paymentMethod === PaymentMethod.ideal && (
                <Alert appearance="info">{language.costInfoAndConsentModal.patientInvoicePreferenceStep.alert}</Alert>
            )}
            {props.state.paymentMethod === PaymentMethod.invoice && (
                <Alert appearance="info">
                    {language.costInfoAndConsentModal.patientInvoicePreferenceStep.askInvoiceAddress}
                </Alert>
            )}
            <Form submitOnEnter={false}>
                <FormGroup label={language.costInfoAndConsentModal.patientInvoicePreferenceStep.receiveVia}>
                    <RadioGroup
                        layout="row"
                        name={'paymentMethod'}
                        onChange={(event) => props.onChange(event.target.value, 'paymentMethod')}
                        sx={responsiveWidthLarge}
                        value={props.state.paymentMethod}
                    >
                        <Choicebox
                            color="primary"
                            flex={1}
                            label={
                                <>
                                    <Heading
                                        mb={2}
                                        size="xsmall"
                                    >
                                        {language.common.email}
                                    </Heading>
                                    <List
                                        size="small"
                                        styleType="bullet"
                                    >
                                        <List.Item>
                                            {language.costInfoAndConsentModal.patientInvoicePreferenceStep.payViaIdeal1}
                                        </List.Item>
                                        <List.Item>
                                            {language.costInfoAndConsentModal.patientInvoicePreferenceStep.payViaIdeal2}
                                        </List.Item>
                                    </List>
                                </>
                            }
                            ribbon={{
                                position: 'top-right',
                                text: 'aanbevolen',
                            }}
                            value="ideal"
                        />
                        <Choicebox
                            color="primary"
                            flex={1}
                            label={
                                <>
                                    <Heading
                                        mb={2}
                                        size="xsmall"
                                    >
                                        {
                                            language.costInfoAndConsentModal.patientInvoicePreferenceStep
                                                .paymentMethodInvoice
                                        }
                                    </Heading>
                                    {language.costInfoAndConsentModal.patientInvoicePreferenceStep.noEmail}
                                </>
                            }
                            value="invoice"
                        />
                    </RadioGroup>
                </FormGroup>
                {props.state.paymentMethod === PaymentMethod.ideal && (
                    <FormGroup
                        hasErrors={props.validationErrors.has('emailAddress')}
                        isRequired={true}
                        label={language.common.emailAddress}
                    >
                        <TextInput
                            error={getValidationErrorTranslation(props.validationErrors.get('emailAddress'))}
                            onBlur={(e) => props.onBlur(e.target.value, 'emailAddress')}
                            onChange={(e) => props.onChange(e.target.value, 'emailAddress')}
                            placeholder={language.common.email}
                            sx={responsiveWidthLarge}
                            value={props.state.emailAddress || ''}
                            variant="outline"
                        />
                    </FormGroup>
                )}
                {props.state.paymentMethod === PaymentMethod.ideal && (
                    <>
                        <FormGroup
                            hasErrors={props.validationErrors.has('homeAddress.postalCode')}
                            isRequired={true}
                            label={language.common.zipcode}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('homeAddress.postalCode')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeHomeAddress(e.target.value.toUpperCase(), 'postalCode'),
                                        'homeAddress',
                                        'homeAddress.postalCode'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(
                                        onchangeHomeAddress(e.target.value.toUpperCase(), 'postalCode'),
                                        'homeAddress'
                                    )
                                }
                                placeholder={language.common.zipcode}
                                sx={responsiveWidthSmall}
                                value={props.state.homeAddress?.postalCode || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('homeAddress.houseNumber')}
                            isRequired={true}
                            label={language.common.houseNumber}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('homeAddress.houseNumber')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeHomeAddress(e.target.value, 'houseNumber'),
                                        'homeAddress',
                                        'homeAddress.houseNumber'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(onchangeHomeAddress(e.target.value, 'houseNumber'), 'homeAddress')
                                }
                                placeholder={language.common.houseNo}
                                sx={responsiveWidthSmall}
                                value={props.state.homeAddress?.houseNumber || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('homeAddress.houseNumberSuffix')}
                            label={language.common.suffix}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('homeAddress.houseNumberSuffix')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeHomeAddress(e.target.value, 'houseNumberSuffix'),
                                        'homeAddress',
                                        'homeAddress.houseNumberSuffix'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(
                                        onchangeHomeAddress(e.target.value, 'houseNumberSuffix'),
                                        'homeAddress'
                                    )
                                }
                                placeholder={language.common.suffixAbbreviation}
                                sx={responsiveWidthSmall}
                                value={props.state.homeAddress?.houseNumberSuffix || ''}
                                variant="outline"
                            />
                        </FormGroup>
                    </>
                )}
                {props.state.paymentMethod === PaymentMethod.invoice && (
                    <>
                        <FormGroup
                            hasErrors={props.validationErrors.has('invoiceAddress.postalCode')}
                            isRequired={true}
                            label={language.common.zipcode}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('invoiceAddress.postalCode')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeInvoiceAddress(e.target.value.toUpperCase(), 'postalCode'),
                                        'invoiceAddress',
                                        'invoiceAddress.postalCode'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(
                                        onchangeInvoiceAddress(e.target.value.toUpperCase(), 'postalCode'),
                                        'invoiceAddress'
                                    )
                                }
                                placeholder={language.common.zipcode}
                                sx={responsiveWidthSmall}
                                value={props.state.invoiceAddress?.postalCode || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('invoiceAddress.houseNumber')}
                            isRequired={true}
                            label={language.common.houseNumber}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('invoiceAddress.houseNumber')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeInvoiceAddress(e.target.value, 'houseNumber'),
                                        'invoiceAddress',
                                        'invoiceAddress.houseNumber'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(
                                        onchangeInvoiceAddress(e.target.value, 'houseNumber'),
                                        'invoiceAddress'
                                    )
                                }
                                placeholder={language.common.houseNo}
                                sx={responsiveWidthSmall}
                                value={props.state.invoiceAddress?.houseNumber || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('invoiceAddress.houseNumberSuffix')}
                            label={language.common.suffix}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('invoiceAddress.houseNumberSuffix')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeInvoiceAddress(e.target.value, 'houseNumberSuffix'),
                                        'invoiceAddress',
                                        'invoiceAddress.houseNumberSuffix'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(
                                        onchangeInvoiceAddress(e.target.value, 'houseNumberSuffix'),
                                        'invoiceAddress'
                                    )
                                }
                                placeholder={language.common.suffixAbbreviation}
                                sx={responsiveWidthSmall}
                                value={props.state.invoiceAddress?.houseNumberSuffix || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('invoiceAddress.street')}
                            isRequired={true}
                            label={language.common.street}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(
                                    props.validationErrors.get('invoiceAddress.street')
                                )}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeInvoiceAddress(e.target.value, 'street'),
                                        'invoiceAddress',
                                        'invoiceAddress.street'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(onchangeInvoiceAddress(e.target.value, 'street'), 'invoiceAddress')
                                }
                                placeholder={language.common.street}
                                sx={responsiveWidthLarge}
                                value={props.state.invoiceAddress?.street || ''}
                                variant="outline"
                            />
                        </FormGroup>
                        <FormGroup
                            hasErrors={props.validationErrors.has('invoiceAddress.city')}
                            isRequired={true}
                            label={language.common.city}
                        >
                            <TextInput
                                error={getValidationErrorTranslation(props.validationErrors.get('invoiceAddress.city'))}
                                onBlur={(e) =>
                                    props.onBlur(
                                        onchangeInvoiceAddress(e.target.value, 'city'),
                                        'invoiceAddress',
                                        'invoiceAddress.city'
                                    )
                                }
                                onChange={(e) =>
                                    props.onChange(onchangeInvoiceAddress(e.target.value, 'city'), 'invoiceAddress')
                                }
                                placeholder={language.common.cityPlaceholder}
                                sx={responsiveWidthLarge}
                                value={props.state.invoiceAddress?.city || ''}
                                variant="outline"
                            />
                        </FormGroup>
                    </>
                )}
            </Form>
        </>
    );

    function onchangeHomeAddress(value: string, name: string) {
        return {
            ...props.state.homeAddress!,
            [name]: value,
        };
    }

    function onchangeInvoiceAddress(value: string, name: string) {
        return {
            ...props.state.invoiceAddress!,
            [name]: value,
        };
    }
}
